import { ExpandLess, ExpandMore, InfoOutlined } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DeliveryCompanyDetailsDialog from "../DeliveryCompanyDetailsDialog";
import BestDeal from "./best-deal.svg";
import DialogCODShipment from "./DialogCODShipment";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "src/_store/entities/profile";
import { ROLES } from "src/constants";

export default function DeliveryCompanyListItem({
  company,
  orderId,
  onAssignedSuccess,
  selectOrder,
}) {

  const hasCOD = selectOrder?.amountPayable && selectOrder?.amountPayable > 0;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { profile } = useSelector((state) => state.entities.profile);

  const handleClick = () => {
    setOpen(!open);
  };

  const isDownMdScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    if (isDownMdScreen) {
      setOpen(false);
    }
  }, [isDownMdScreen]);

  const [
    openDeliveryCompanyDialogDetails,
    setOpenDeliveryCompanyDialogDetails,
  ] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [openCODCheck, setOpenCODCheck] = useState(false);

  const openDeliveryCompanyDialogDetailsHandler = () => {
    setOpenDeliveryCompanyDialogDetails(true);
    setSelectedCompany(company);
  };

  const dispatch = useDispatch();
  const openCODShipmentHandler = () => {

    if (profile?.merchant?.role === ROLES.merchant) {
      if (profile?.merchant?.hasCodShipment === false && hasCOD) {
        setOpenCODCheck(true);
      } else {
        openDeliveryCompanyDialogDetailsHandler();
      }
    } else {
      openDeliveryCompanyDialogDetailsHandler();
    }
  };

  const closeDeliveryCompanyDialogDetailsHandler = () => {
    setOpenDeliveryCompanyDialogDetails(false);
    setSelectedCompany(null);
    setOpenCODCheck(false);
  };

  const handleCODConfirm = () => {
    setOpenCODCheck(false);
    openDeliveryCompanyDialogDetailsHandler();
  };

  const isSpecialOffer = useMemo(() => {
    // Aramex delivery option IDs [5551, 1618]
    // SMSA delivery option IDs [2828, 5556]
    //[5551, 1618, 2828, 5556].includes(company?.deliveryOptionId) &&
    return ["aramex", "secom", "Redbox"].includes(company?.integrationType)
  }, [company]);

  const getPrice = (company) => {
    // Aramex delivery option IDs [5551, 1618]
    if (
      [5551, 1618].includes(company?.deliveryOptionId) &&
      company?.integrationType === "aramex" &&
      Number(company?.price) === 17.09
    ) {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="subtitle2"
            color="error"
            fontWeight="bold"
            sx={{ textDecoration: "line-through" }}
          >
            18.95
          </Typography>
          <Typography variant="subtitle2">17.09</Typography>
        </Box>
      );
    } else if (
      // RedBox delivery option ID [7177]
      company?.deliveryOptionId === 7177 &&
      company?.integrationType === "Redbox" &&
      Number(company?.price) === 12
    ) {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            variant="subtitle2"
            color="error"
            fontWeight="bold"
            sx={{ textDecoration: "line-through" }}
          >
            13.2
          </Typography>
          <Typography variant="subtitle2">12</Typography>
        </Box>
      );
    } else {
      return (
        <Typography noWrap variant="subtitle2">
          {company?.price}
        </Typography>
      );
    }
  };

  return (
    <>
      <DeliveryCompanyDetailsDialog
        orderId={orderId}
        open={openDeliveryCompanyDialogDetails}
        onClose={closeDeliveryCompanyDialogDetailsHandler}
        selectedCompany={selectedCompany}
        onAssignedSuccess={() => {
          // let parent know this order was assigned
          if (onAssignedSuccess && orderId) {
            onAssignedSuccess(orderId)
            dispatch(
              updateProfile({
                ...profile,
                merchant: {
                  ...profile.merchant,
                  hasCodShipment: true,
                },
              }),
            );
          }
          closeDeliveryCompanyDialogDetailsHandler();
        }}
      />
      <Box
        display="flex"
        flexDirection="column"
        bgcolor="background.paper"
        borderBottom="1px solid #0000001f"
        sx={{
          "&:hover": {
            backgroundColor: "#F9FAFB",
          },
        }}
      >
        <Grid container gap={1} alignItems="center" sx={{ py: 1, px: 1 }}>
          <Grid xs={5} sm={5} md={2.2} item>
            <Box display="flex" alignItems="center">
              <Avatar
                sx={{ width: 50, height: 50, mr: 1 }}
                variant="square"
                src={company?.logo}
              />
              <Tooltip title={company?.deliveryOptionName} placement="top">
                <Typography
                  noWrap={!company?.deliveryOptionName.includes(" ")}
                  variant="subtitle2"
                  sx={{ maxWidth: "100%" }}
                >
                  {company?.deliveryOptionName}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>
          {!isDownMdScreen && (
            <>
              <Grid md={1} item>
                <Tooltip title={company?.serviceType} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ maxWidth: "100%" }}
                  >
                    {company?.serviceType}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid md={1.2} item>
                <Tooltip title={company?.avgDeliveryTime} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ maxWidth: "100%" }}
                  >
                    {company?.avgDeliveryTime}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid md={1.1} item>
                <Tooltip title={company?.minimumPackages} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ maxWidth: "100%" }}
                  >
                    {company?.minimumPackages}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid md={2} item>
                <Tooltip title={company?.pickupDropoff} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ maxWidth: "95%" }}
                  >
                    {company?.pickupDropoff}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid md={1.5} item>
                <Tooltip title={company?.deliveryType} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ maxWidth: "100%" }}
                  >
                    {company?.deliveryType}
                  </Typography>
                </Tooltip>
              </Grid>
            </>
          )}
          <Grid
            xs={3}
            sm={3}
            md={1}
            item
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
            }}
          >
            <Tooltip
              title={isSpecialOffer ? t("Best Deal!") : company?.price}
              placement="top"
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {getPrice(company)}
                {isSpecialOffer ? (
                  <Avatar
                    sx={{ width: 13, height: 25, ml: 1, mb: 1 }}
                    variant="square"
                    src={BestDeal}
                  />
                ) : null}
              </Box>
            </Tooltip>
          </Grid>
          <Grid xs={2} sm={2} md={0.8} item>
            <Button
              variant="contained"
              sx={{ textTransform: "none" }}
              size="small"
              onClick={openCODShipmentHandler}
            >
              {!!orderId ? t("Select") : t("Details")}
            </Button>
            <DialogCODShipment
              open={openCODCheck}
              onConfirm={handleCODConfirm}
            />
          </Grid>
          {isDownMdScreen && (
            <Grid xs={2} sm={1} item>
              <Box textAlign="end">
                <IconButton onClick={handleClick}>
                  {open ? (
                    <ExpandLess color="primary" />
                  ) : (
                    <ExpandMore color="primary" />
                  )}
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>
        <Collapse
          in={open}
          timeout="auto"
          unmountOnExit
          sx={{ backgroundColor: "#F4F6F8 !important", pb: 2, px: 2 }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mt: 1 }}
          >
            <Grid xs={6} md={2} item>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <InfoOutlined size="small" sx={{ mr: 1 }} />
                  {t("Serivce type")}
                </Typography>
                <Tooltip title={company?.serviceType} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ ml: 4, maxWidth: "100%" }}
                  >
                    {company?.serviceType}
                  </Typography>
                </Tooltip>
              </Box>
            </Grid>
            <Grid xs={6} md={2} item>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <InfoOutlined size="small" sx={{ mr: 1 }} />
                  {t("Delivery time")}
                </Typography>
                <Tooltip title={company?.avgDeliveryTime} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ ml: 4, maxWidth: "100%" }}
                  >
                    {company?.avgDeliveryTime}
                  </Typography>
                </Tooltip>
              </Box>
            </Grid>
            <Grid xs={6} md={2} item>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <InfoOutlined size="small" sx={{ mr: 1 }} />
                  {t("Minimum Packages")}
                </Typography>
                <Tooltip
                  title={company?.minimumPackages ?? "-"}
                  placement="top"
                >
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ ml: 4, maxWidth: "100%" }}
                  >
                    {company?.minimumPackages ?? "-"}
                  </Typography>
                </Tooltip>
              </Box>
            </Grid>
            <Grid xs={6} md={2} item>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <InfoOutlined size="small" sx={{ mr: 1 }} />
                  {t("Pickedup Methods")}
                </Typography>
                <Tooltip title={company?.pickupDropoff} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ ml: 4, maxWidth: "100%" }}
                  >
                    {company?.pickupDropoff}
                  </Typography>
                </Tooltip>
              </Box>
            </Grid>
            <Grid xs={12} md={2} item>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <InfoOutlined size="small" sx={{ mr: 1 }} />
                  {t("Delivery Method")}
                </Typography>
                <Tooltip title={company?.deliveryType} placement="top">
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ ml: 4, maxWidth: "100%" }}
                  >
                    {company?.deliveryType}
                  </Typography>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Box>
    </>
  );
}
